/**
 * Utility to clear old caches and force fresh content
 * This helps ensure users see the latest logos and assets
 */

const CACHE_VERSION = 'v2';
const CURRENT_CACHE_NAMES = [
  `google-fonts-cache-${CACHE_VERSION}`,
  `gstatic-fonts-cache-${CACHE_VERSION}`,
  `images-cache-${CACHE_VERSION}`,
  `static-resources-${CACHE_VERSION}`,
  `audio-cache-${CACHE_VERSION}`,
  `data-cache-${CACHE_VERSION}`,
  `api-cache-${CACHE_VERSION}`,
  `pages-cache-${CACHE_VERSION}`,
];

export const clearOldCaches = async (): Promise<void> => {
  if (!('caches' in window)) return;

  try {
    const cacheNames = await caches.keys();
    
    // Delete any cache that isn't in our current version list
    const cachesToDelete = cacheNames.filter(name => {
      // Keep workbox precache (managed by workbox)
      if (name.includes('workbox-precache')) return false;
      // Delete if it doesn't include our current version
      if (!name.includes(CACHE_VERSION)) return true;
      // Keep current version caches
      return false;
    });

    if (cachesToDelete.length > 0) {
      console.log(`🧹 Clearing ${cachesToDelete.length} old cache(s):`, cachesToDelete);
      await Promise.all(cachesToDelete.map(name => caches.delete(name)));
      console.log('✅ Old caches cleared successfully');
    }
  } catch (error) {
    console.error('Error clearing old caches:', error);
  }
};

export const forceRefreshServiceWorker = async (): Promise<void> => {
  if (!('serviceWorker' in navigator)) return;

  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (registration) {
      // Force update check
      await registration.update();
      
      // If there's a waiting worker, activate it immediately
      if (registration.waiting) {
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        console.log('🔄 New service worker activated');
      }
    }
  } catch (error) {
    console.error('Error updating service worker:', error);
  }
};

export const clearImageCache = async (): Promise<void> => {
  if (!('caches' in window)) return;

  try {
    const cacheNames = await caches.keys();
    const imageCaches = cacheNames.filter(name => name.includes('images'));
    
    await Promise.all(imageCaches.map(name => caches.delete(name)));
    console.log('🖼️ Image caches cleared');
  } catch (error) {
    console.error('Error clearing image caches:', error);
  }
};

// Initialize cache clearing on app load
export const initCacheClearing = (): void => {
  // Run cache cleanup immediately
  clearOldCaches();
  
  // Force service worker update
  forceRefreshServiceWorker();
  
  // Also clear image cache to ensure fresh logos
  clearImageCache();
  
  // Set up periodic cache check (every 5 minutes while app is open)
  setInterval(() => {
    forceRefreshServiceWorker();
  }, 5 * 60 * 1000);
};
